import React, { useState, useEffect, useCallback, forwardRef } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  IconButton,
  Tooltip,
  useToast,
  Select,
  Container,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Stack,
  Input,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useBreakpointValue,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import { ViewIcon, DownloadIcon, SearchIcon, CalendarIcon, TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { api, getOrcamentos, baixarOrcamento } from "../services/api";
import Preloader from "../components/Preloader";
import ScrollToTop from "../components/ScrollToTop";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ptBR from 'date-fns/locale/pt-BR';
registerLocale('pt-BR', ptBR);

const formatMoeda = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatData = (d) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const ListaOrcamentos = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [orcamentos, setOrcamentos] = useState([]);
  const [stats, setStats] = useState({ total_orcamentos: 0, valor_total: 0, total_web: 0, total_bot: 0 });
  const [geradores, setGeradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [downloadingId, setDownloadingId] = useState(null);

  const [detalhe, setDetalhe] = useState(null);
  const [detalheLoading, setDetalheLoading] = useState(false);

  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });

  const [filtros, setFiltros] = useState({
    busca: '',
    origem: 'todas',
    user_id: 'todos',
    data_inicio: null,
    data_fim: null,
  });

  const isMobile = useBreakpointValue({ base: true, lg: false });

  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <InputGroup onClick={onClick} ref={ref}>
      <InputLeftElement pointerEvents="none">
        <CalendarIcon color="gray.300" />
      </InputLeftElement>
      <Input value={value} placeholder="Selecione o período..." bg="gray.50" readOnly cursor="pointer" />
    </InputGroup>
  ));

  const fetchOrcamentos = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        busca: filtros.busca,
        origem: filtros.origem,
        user_id: filtros.user_id,
        sort: sort.field,
        dir: sort.dir,
        data_inicio: filtros.data_inicio ? filtros.data_inicio.toISOString().split('T')[0] : '',
        data_fim: filtros.data_fim ? filtros.data_fim.toISOString().split('T')[0] : '',
      };

      const data = await getOrcamentos(params);
      if (data && data.orcamentos) {
        setOrcamentos(data.orcamentos.data);
        setPagination({
          current_page: data.orcamentos.current_page,
          last_page: data.orcamentos.last_page,
          total: data.orcamentos.total,
        });
        setStats(data.stats || stats);
        setGeradores(data.geradores || []);
      } else {
        toast({ title: "Erro ao carregar orçamentos", status: "error", duration: 3000, isClosable: true });
      }
    } catch (error) {
      console.error(error);
      toast({ title: "Erro ao carregar orçamentos", status: "error", duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, sort, toast]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchOrcamentos(1);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [filtros, sort, fetchOrcamentos]);

  const handleFilterChange = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value }));
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFiltros(prev => ({ ...prev, data_inicio: start, data_fim: end }));
  };

  const handleSort = (field) => {
    setSort(prev => prev.field === field
      ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { field, dir: 'asc' });
  };

  const handleDownload = async (codigo) => {
    setDownloadingId(codigo);
    try {
      await baixarOrcamento(codigo);
    } catch (error) {
      toast({ title: "Erro ao baixar PDF", description: error.message, status: "error", isClosable: true });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleVerDetalhes = async (orc) => {
    setDetalhe({ orc, produtos: [], cliente: null });
    setDetalheLoading(true);
    onOpen();
    try {
      const res = await api.get(`/produtos_orcamento`, { params: { codigo: orc.codigo } });
      if (res.data && res.data.produtos) {
        setDetalhe({ orc, produtos: res.data.produtos, cliente: res.data.dados_cliente });
      }
    } catch (error) {
      toast({ title: "Erro ao carregar detalhes", status: "error", isClosable: true });
    } finally {
      setDetalheLoading(false);
    }
  };

  const getOrigemBadge = (origem) => origem === 'bot'
    ? <Badge colorScheme="purple" variant="subtle">Bot / IA</Badge>
    : <Badge colorScheme="blue" variant="subtle">Web / ADM</Badge>;

  const getGeradoPor = (orc) => orc.gerado_por || (orc.origem === 'bot' ? 'Bot de Atendimento' : 'N/I');

  const SortHeader = ({ field, children, isNumeric }) => (
    <Th isNumeric={isNumeric} cursor="pointer" userSelect="none" onClick={() => handleSort(field)} _hover={{ color: "orange.500" }}>
      <Flex align="center" gap={1} justify={isNumeric ? "flex-end" : "flex-start"}>
        {children}
        {sort.field === field && (sort.dir === 'asc' ? <TriangleUpIcon boxSize={3} /> : <TriangleDownIcon boxSize={3} />)}
      </Flex>
    </Th>
  );

  const renderTable = () => (
    <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor="gray.200">
      <Table variant="simple" bg="white">
        <Thead bg="gray.50">
          <Tr>
            <SortHeader field="codigo">Código</SortHeader>
            <SortHeader field="nome_cliente">Cliente</SortHeader>
            <Th>Gerado por</Th>
            <Th>Origem</Th>
            <SortHeader field="qtd_itens" isNumeric>Itens</SortHeader>
            <SortHeader field="total" isNumeric>Total</SortHeader>
            <SortHeader field="created_at">Data</SortHeader>
            <Th width="120px">Ações</Th>
          </Tr>
        </Thead>
        <Tbody>
          {orcamentos.map((orc) => (
            <Tr key={orc.id} _hover={{ bg: "gray.50" }}>
              <Td fontWeight="bold" color="orange.500">{orc.codigo}</Td>
              <Td fontWeight="semibold">{orc.nome_cliente || 'N/I'}</Td>
              <Td fontSize="sm" color="gray.600">{getGeradoPor(orc)}</Td>
              <Td>{getOrigemBadge(orc.origem)}</Td>
              <Td isNumeric>{orc.qtd_itens ?? '-'}</Td>
              <Td isNumeric fontWeight="bold">{formatMoeda(orc.total)}</Td>
              <Td fontSize="sm" color="gray.600">{formatData(orc.created_at)}</Td>
              <Td>
                <Flex gap={1}>
                  <Tooltip label="Ver Detalhes">
                    <IconButton aria-label="Ver" icon={<ViewIcon />} size="sm" colorScheme="blue" variant="ghost" onClick={() => handleVerDetalhes(orc)} />
                  </Tooltip>
                  <Tooltip label="Baixar PDF">
                    <IconButton aria-label="Baixar" icon={<DownloadIcon />} size="sm" colorScheme="orange" variant="ghost" isLoading={downloadingId === orc.codigo} onClick={() => handleDownload(orc.codigo)} />
                  </Tooltip>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );

  const renderCards = () => (
    <SimpleGrid columns={1} spacing={4}>
      {orcamentos.map((orc) => (
        <Card key={orc.id} shadow="sm" border="1px solid" borderColor="gray.200">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Heading size="sm" color="orange.500">{orc.codigo}</Heading>
              {getOrigemBadge(orc.origem)}
            </Flex>
          </CardHeader>
          <CardBody py={2}>
            <Stack spacing={2} fontSize="sm">
              <Flex justify="space-between"><Text color="gray.500">Cliente:</Text><Text fontWeight="bold" textAlign="right">{orc.nome_cliente || 'N/I'}</Text></Flex>
              <Flex justify="space-between"><Text color="gray.500">Gerado por:</Text><Text>{getGeradoPor(orc)}</Text></Flex>
              <Flex justify="space-between"><Text color="gray.500">Itens:</Text><Text fontWeight="bold">{orc.qtd_itens ?? '-'}</Text></Flex>
              <Flex justify="space-between"><Text color="gray.500">Total:</Text><Text fontWeight="bold" color="green.600">{formatMoeda(orc.total)}</Text></Flex>
              <Flex justify="space-between"><Text color="gray.500">Data:</Text><Text>{formatData(orc.created_at)}</Text></Flex>
            </Stack>
          </CardBody>
          <CardFooter pt={2} justify="flex-end" borderTop="1px solid #f0f0f0">
            <HStack spacing={1}>
              <Button size="sm" leftIcon={<ViewIcon />} variant="ghost" colorScheme="blue" onClick={() => handleVerDetalhes(orc)}>Ver</Button>
              <Button size="sm" leftIcon={<DownloadIcon />} variant="ghost" colorScheme="orange" isLoading={downloadingId === orc.codigo} onClick={() => handleDownload(orc.codigo)}>PDF</Button>
            </HStack>
          </CardFooter>
        </Card>
      ))}
    </SimpleGrid>
  );

  return (
    <Box bg="gray.50" minH="100vh" py={6}>
      <Container maxW="container.xl">

        <Flex justify="space-between" align="center" mb={6} direction={{ base: "column", md: "row" }} gap={4}>
          <Box>
            <Heading size="lg" color="orange.500">Orçamentos</Heading>
          </Box>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Card p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="orange.400">
            <Stat><StatLabel color="gray.500">Total de Orçamentos</StatLabel><StatNumber color="orange.500">{stats.total_orcamentos || 0}</StatNumber><StatHelpText>Todos os períodos</StatHelpText></Stat>
          </Card>
          <Card p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="green.400">
            <Stat><StatLabel color="gray.500">Valor Total</StatLabel><StatNumber color="green.500" fontSize="2xl">{formatMoeda(stats.valor_total)}</StatNumber><StatHelpText>Somatório geral</StatHelpText></Stat>
          </Card>
          <Card p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="blue.400">
            <Stat><StatLabel color="gray.500">Web / ADM</StatLabel><StatNumber color="blue.500">{stats.total_web || 0}</StatNumber><StatHelpText>Gerados no sistema</StatHelpText></Stat>
          </Card>
          <Card p={4} variant="outline" bg="white" borderLeft="4px solid" borderColor="purple.400">
            <Stat><StatLabel color="gray.500">Bot / IA</StatLabel><StatNumber color="purple.500">{stats.total_bot || 0}</StatNumber><StatHelpText>Atendimento WhatsApp</StatHelpText></Stat>
          </Card>
        </SimpleGrid>

        <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} alignItems="end">
            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>Busca</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none"><SearchIcon color="gray.300" /></InputLeftElement>
                <Input placeholder="Código ou cliente..." value={filtros.busca} onChange={(e) => handleFilterChange('busca', e.target.value)} bg="gray.50" />
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>Origem</FormLabel>
              <Select value={filtros.origem} onChange={(e) => handleFilterChange('origem', e.target.value)} bg="gray.50">
                <option value="todas">Todas Origens</option>
                <option value="web">Web / ADM</option>
                <option value="bot">Bot / IA</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>Gerado por</FormLabel>
              <Select value={filtros.user_id} onChange={(e) => handleFilterChange('user_id', e.target.value)} bg="gray.50">
                <option value="todos">Todos</option>
                {geradores.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>Período</FormLabel>
              <DatePicker
                selectsRange={true}
                startDate={filtros.data_inicio}
                endDate={filtros.data_fim}
                onChange={handleDateRangeChange}
                isClearable={true}
                locale="pt-BR"
                dateFormat="dd/MM/yyyy"
                customInput={<CustomDateInput />}
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        {loading ? (
          <Flex justify="center" py={20}><Preloader /></Flex>
        ) : (
          <>
            {orcamentos.length === 0 ? (
              <Flex bg="white" p={10} justify="center" align="center" borderRadius="lg" border="1px dashed" borderColor="gray.300" direction="column">
                <Text fontSize="lg" color="gray.500" mb={4}>Nenhum orçamento encontrado com os filtros atuais.</Text>
                <Button variant="outline" onClick={() => setFiltros({ busca: '', origem: 'todas', user_id: 'todos', data_inicio: null, data_fim: null })}>
                  Limpar Filtros
                </Button>
              </Flex>
            ) : (
              isMobile ? renderCards() : renderTable()
            )}

            {pagination.total > 15 && (
              <Flex justify="space-between" align="center" mt={6}>
                <Button isDisabled={pagination.current_page === 1} onClick={() => fetchOrcamentos(pagination.current_page - 1)} size="sm" variant="outline">Anterior</Button>
                <Text fontSize="sm" color="gray.600">Página <b>{pagination.current_page}</b> de <b>{pagination.last_page}</b> ({pagination.total} orçamentos)</Text>
                <Button isDisabled={pagination.current_page === pagination.last_page} onClick={() => fetchOrcamentos(pagination.current_page + 1)} size="sm" variant="outline">Próxima</Button>
              </Flex>
            )}
          </>
        )}

        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "xl" }} scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader color="orange.500">Orçamento {detalhe?.orc?.codigo}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {detalheLoading ? (
                <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
              ) : (
                <>
                  {detalhe?.cliente && (
                    <Box mb={4} fontSize="sm">
                      <Text><b>Cliente:</b> {detalhe.cliente.nome || 'N/I'}</Text>
                      {detalhe.cliente.documento && <Text><b>Documento:</b> {detalhe.cliente.documento}</Text>}
                      {detalhe.cliente.telefone && <Text><b>Telefone:</b> {detalhe.cliente.telefone}</Text>}
                      {detalhe.cliente.email && <Text><b>Email:</b> {detalhe.cliente.email}</Text>}
                      {detalhe.cliente.endereco && <Text><b>Endereço:</b> {detalhe.cliente.endereco}</Text>}
                      {detalhe.cliente.observacoes && <Text><b>Obs:</b> {detalhe.cliente.observacoes}</Text>}
                    </Box>
                  )}
                  <Divider mb={3} />
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Produto</Th>
                          <Th>Cód. Global</Th>
                          <Th isNumeric>Qtd</Th>
                          <Th isNumeric>V. Unit.</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(detalhe?.produtos || []).map((p, i) => (
                          <Tr key={i}>
                            <Td>{p.nome}</Td>
                            <Td>{p.codigo_global}</Td>
                            <Td isNumeric>{p.quantidade_orcamento ?? '-'}</Td>
                            <Td isNumeric>{formatMoeda(p.valor_venda)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                    {(!detalhe?.produtos || detalhe.produtos.length === 0) && (
                      <Text color="gray.500" fontSize="sm" py={4} textAlign="center">Nenhum item disponível para exibição.</Text>
                    )}
                  </Box>
                  <Flex justify="flex-end" mt={4} fontWeight="bold" fontSize="lg" color="green.600">
                    Total: {formatMoeda(detalhe?.orc?.total)}
                  </Flex>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>Fechar</Button>
              <Button colorScheme="orange" leftIcon={<DownloadIcon />} isLoading={downloadingId === detalhe?.orc?.codigo} onClick={() => handleDownload(detalhe?.orc?.codigo)}>Baixar PDF</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

      </Container>
      <ScrollToTop />
    </Box>
  );
};

export default ListaOrcamentos;
